/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSnackbar } from "react-simple-snackbar";
import { InfoContainer, Layout, Modal, Text } from "$components";
import SideMenu from "$components/SideMenuProductList/SideMenu";
import { IProduct } from "$containers";
import useJStyles from "$hooks/useJStyles";
import useURLQuery from "$hooks/useURLQuery";
import useAuth from "$hooks/useAuth";
import { useWMSConfigQuery } from "$hooks/useWMSConfigQuery";
import {
  snackbarErrorOptions,
  snackbarSuccessOptions,
} from "$utils/SnackbarOptions";
import productApi from "../../api/productApi";
import { CategoryTableProduct } from "./CategoryTableProduct";
import RightHeader from "./RightHeader";
import { WrapCatalogueActions } from "./WrapCatalogueActions";

interface RouteParams {
  idCompany?: string;
  idProduct?: string;
}

function Catalogue() {
  const params = useParams<RouteParams>();
  const urlQuery = useURLQuery();
  const contributorId = urlQuery.get("contributor");
  const { idCompany: urlCompanyId } = params;
  const { jclasses } = useJStyles();
  const { token, isAdmin, isClient } = useAuth();
  const [openModalDeleteProduct, setOpenModalDeleteProduct] = useState(false);
  const [selectedDeleteProduct, setSelectedDeleteProduct] =
    useState<any>(undefined);
  const [openSnackbarError] = useSnackbar(snackbarErrorOptions);
  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);
  const { data: wmsConfigs = [] } = useWMSConfigQuery();
  const [filterText, setFilterText] = useState<string>("");
  const [filterByCateg, setFilterByCateg] = useState<string>("");
  const [filterIsActive, setFilterIsActive] = useState<boolean>(true);

  const deleteProduct = async (product: IProduct) => {
    const { _id, reference } = product;
    const deleteProductRes = await productApi.deleteProduct(token, _id);
    if (deleteProductRes.status === 403) {
      openSnackbarError(
        `Le produit ${reference} est déjà utilisé dans une EDB, impossible de le supprimer.`,
        5000
      );
    } else {
      openSnackbar(`Le produit ${reference} a bien été supprimé.`, 5000);
    }
    setOpenModalDeleteProduct(false);
    setSelectedDeleteProduct(undefined);
  };

  const catalogWMS = useMemo(() => {
    if (urlCompanyId && Array.isArray(wmsConfigs)) {
      return wmsConfigs.filter((config: any) =>
        config?.config?.providers?.find(
          (provider: any) => provider?.companyId === urlCompanyId
        )
      );
    }
    return [];
  }, [urlCompanyId, wmsConfigs]);

  const renderHeaderMemo = useMemo(
    () => (
      <RightHeader
        companyId={urlCompanyId}
        contributorId={contributorId}
        catalogWMS={catalogWMS}
      />
    ),
    [contributorId, urlCompanyId, catalogWMS]
  );

  const rightHeaderMemo = useMemo(
    () =>
      (isAdmin() || isClient()) && (
        <WrapCatalogueActions
          companyId={urlCompanyId}
          filterText={filterText}
          filterByCateg={filterByCateg}
          filterIsActive={filterIsActive}
          setFilterIsActive={setFilterIsActive}
        />
      ),
    [filterText, filterByCateg, filterIsActive, isAdmin, isClient, urlCompanyId]
  );

  return (
    <div className={jclasses.jWrapper}>
      <Layout renderHead={() => renderHeaderMemo} title="Catalogue">
        <InfoContainer rightHeader={rightHeaderMemo}>
          {!urlCompanyId && (
            <Alert severity="error">
              Veuillez sélectionner une entreprise.
            </Alert>
          )}
          {urlCompanyId && (
            <Box display="flex">
              <SideMenu
                filterSearch={filterText}
                setFilterSearch={setFilterText}
                filterByCategory={filterByCateg}
                setFilterByCategory={setFilterByCateg}
                companyId={urlCompanyId}
              />

              <CategoryTableProduct
                companyId={urlCompanyId}
                catalogWMS={catalogWMS}
                setSelectedDeleteProduct={setSelectedDeleteProduct}
                setOpenModalDeleteProduct={setOpenModalDeleteProduct}
                filterText={filterText}
                filterByCateg={filterByCateg}
                filterIsActive={filterIsActive}
              />
            </Box>
          )}
        </InfoContainer>

        <Modal
          textValidate="Supprimer"
          textCancel="Annuler"
          open={openModalDeleteProduct}
          onValidate={async () => {
            if (selectedDeleteProduct) {
              await deleteProduct(selectedDeleteProduct);
            }
          }}
          onCancel={() => setOpenModalDeleteProduct(false)}
        >
          <div className={jclasses.jDialogContent}>
            <Text>
              Attention
              <br />
              Vous êtes sur le point de supprimer définitivement ce produit du
              catalogue.
              <br />
              Êtes-vous sûr de vouloir continuer ?
            </Text>
          </div>
        </Modal>
      </Layout>
    </div>
  );
}

export default Catalogue;
